import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AriOrb from "@/components/ari/AriOrb";

const ORB_STATE = { observing: "idle", active: "thinking", detected: "insight", waiting: "responding", idle: "idle" };

const STATE_STYLE = {
  observing: { dot: "#8A9A5B", ring: "rgba(138,154,91,0.5)", pulse: true },
  active: { dot: "#8A9A5B", ring: "rgba(138,154,91,0.5)", pulse: true },
  detected: { dot: "#D97706", ring: "rgba(217,119,6,0.5)", pulse: true },
  waiting: { dot: "#D4AF37", ring: "rgba(212,175,55,0.55)", pulse: true },
  idle: { dot: "#6B7280", ring: "rgba(107,114,128,0.4)", pulse: false },
};

// Live, real-state presence for ARI in the app shell. Every state maps to a real backend value
// from /api/ari/status — never a decorative animation.
export default function AriStatusPill() {
  const nav = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/ari/status").then(({ data }) => alive && setS(data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!s) return null;
  const st = STATE_STYLE[s.state] || STATE_STYLE.observing;
  const attention = s.counts?.needs_attention || 0;
  const pending = s.counts?.pending_approval || 0;
  const badge = pending > 0 ? pending : attention > 0 ? attention : 0;

  return (
    <button
      data-testid="ari-status-pill"
      data-ari-state={s.state}
      onClick={() => nav("/ari")}
      title={`${s.headline} — ${s.detail}`}
      className="group relative flex items-center gap-2.5 rounded-full border border-white/10 bg-[#141416]/80 pl-3 pr-3.5 py-1.5 hover:border-[#D4AF37]/40 transition-colors duration-300"
    >
      <span className="relative flex items-center justify-center shrink-0">
        <AriOrb size={30} state={ORB_STATE[s.state] || "idle"} rings particles />
      </span>
      <span className="hidden sm:flex flex-col items-start leading-tight">
        <span className="text-[9px] uppercase tracking-[0.2em] text-white/40">ARI</span>
        <span className="text-xs font-medium text-white/85 -mt-0.5">{s.headline}</span>
      </span>
      <span className="sm:hidden text-xs font-semibold text-white/85">ARI</span>
      {badge > 0 && (
        <span data-testid="ari-pill-badge"
              className="ml-0.5 min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center"
              style={{ backgroundColor: pending > 0 ? "#D4AF37" : "#D97706", color: "#0A0A0B" }}>
          {badge}
        </span>
      )}
    </button>
  );
}
